import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { WorkflowTabs, type WorkflowTab } from '@/components/WorkflowTabs'
import { GeneratorFrame, PriceConfirmDialog, StudioPanel, SubmitBar } from '@/components/studio'
import { RefUploader } from '@/components/RefUploader'
import { ProductInfoSection } from '@/components/ProductInfoSection'
import {
  Badge,
  Card,
  CardBody,
  CardHead,
  Chips,
  CountInput,
  Field,
  GroupedSelect,
  RatioInput,
  Segmented,
  Select,
  Textarea,
} from '@/components/ui'
import { AiFillButton } from '@/components/AiFillButton'
import { Icon } from '@/components/icons'
import { ModelSelect } from '@/components/ModelSelect'
import { DEFAULT_FAMILY_ID } from '@/config/modelFamilies'
import {
  DEFAULT_PLATFORM_ID,
  DOMESTIC_PLATFORMS,
  OVERSEAS_PLATFORMS,
  getPlatform,
} from '@/config/platforms'
import {
  CATEGORY_LABELS,
  IMAGE_RESOLUTIONS,
  featuresOf,
  findFeature,
  modelLabel,
  type StudioFeature,
} from '@/config/catalog'
import { buildFeatureFillBrief, buildFeaturePrompt } from '@/config/prompts'
import { FILL_FORM_FIELDS, type ProductAsset } from '@/api/types'
import { ProductAssetPickerButton } from '@/components/ProductAssetPicker'
import { useToast } from '@/context/ToastContext'
import { mergeProductRefs, productImportMessage } from '@/lib/productRefs'
import { refillFromInputs } from '@/lib/refill'
import {
  MODEL_LOOK_DEFAULT,
  MODEL_LOOK_GROUPS,
  modelLookText,
  modelShotPlan,
  type ModelLook,
} from '@/config/modelProfile'
import {
  SCENE_DEFAULT,
  SCENE_STYLES,
  SCENE_TYPES,
  sceneText,
  subsOf,
  type ScenePick,
} from '@/config/sceneProfile'
import { useAssetLibrary } from '@/context/AssetLibraryContext'
import { useJobCenter, type Job } from '@/context/JobCenterContext'
import { imgHostLabel } from '@/lib/mediaUrl'
import { useDraft } from '@/hooks/useDraft'
import { useGenerateFlow } from '@/hooks/useGenerateFlow'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { usePricePreview } from '@/hooks/usePricePreview'

type FormState = {
  feature: string
  /** 投放平台 —— 决定图内文字语言与合规禁词 */
  platform: string
  requirement: string
  aspectRatio: string
  resolution: '1k' | '2k' | '4k'
  outputs: number
  model: string
  primary: string[]
  secondary: string[]
  /**
   * 参考图来自「同页另一个工作流的产出」时放在这里（见 `StudioFeature.primaryFrom`）。
   *
   * 为什么必须与 `primary` 分开存：`primary` 是**用户上传的素材**，
   * 换工作流时刻意不清空（换档只换参数，素材留着继续用）。
   * 场景图的参考图却是「上一档 AI 模特图的产出」——两者来源不同，
   * 塞进同一个数组的话，从「白底主图」带着上传图切到「场景图」，
   * 界面会说「参考图来自 AI 模特图」而实际传的是用户那张上传图，
   * 这种「文案与事实不符」在本项目比崩溃更难查（截图上看不出来）。
   */
  outputRefs: string[]
  /**
   * 结构化模特设定（只有 `StudioFeature.modelLook` 的档会渲染选择器）。
   *
   * 取值真源在 `config/modelProfile.ts`；这里存的是**用户选中的那一份**，
   * 进 `inputs` 一起提交。八项都是选择器，所以没有「填一半」的中间态 ——
   * 默认值是「不限 / 自动」，判定「有没有指定」用 `isModelLookSet`，
   * 不在页面里自己比字符串。老草稿没有这个键时由 `useDraft` 的浅合并兜住。
   */
  look: ModelLook
  /**
   * 结构化场景设定（只有 `StudioFeature.scenePlan` 的档会渲染选择器）。
   *
   * 取值真源在 `config/sceneProfile.ts`；默认是「AI 智能匹配」（等于不指定，
   * 不写进提示词）。老草稿没有这个键时由 `useDraft` 的浅合并兜住。
   */
  scene: ScenePick
}

const DEFAULT_MODEL = DEFAULT_FAMILY_ID.image

/** 平台清单按国内外分组，避免海外平台混在国内名单里被误选 */
const PLATFORM_GROUPS = [
  { label: '国内平台', list: DOMESTIC_PLATFORMS },
  { label: '海外平台', list: OVERSEAS_PLATFORMS },
]

/**
 * 三个一层页签 + 常驻提交栏。
 *
 * 2026-09-28 二改：用户反馈「左边的 form 太长了，操作不友好」——
 * 上一版只有 2 个页签，第 1 个把「参考素材」与「出图参数」两块 Card 叠在一起，
 * 左栏（参考图 + 第二参考图 + 文字描述 + 模型 + 平台 + 比例 + 分辨率 + 数量）
 * 一屏放不下，必填项与可选项混在一起看不出重点。现在按「先交素材 → 再定参数 →
 * 最后核对」拆成三个页签，每屏只回答一个问题。
 *
 * 保留的旧结论：必填项随工作流变（8 个服饰工作流有的要主图、有的要第二张、
 * 有的必须写文字），所以页签 1 的字段由 `feature` 驱动，不是固定表单。
 */
const PANES: WorkflowTab[] = [
  { id: 'assets', title: '参考素材', desc: '按当前工作流上传参考图与文字描述' },
  { id: 'config', title: '出图参数', desc: '模型、投放平台、画面比例、分辨率与输出数量' },
  { id: 'review', title: '核对与生成', desc: '参数核对后提交，失败不扣费' },
]

function defaultsFor(f: StudioFeature): FormState {
  return {
    feature: f.id,
    platform: DEFAULT_PLATFORM_ID,
    requirement: '',
    aspectRatio: f.ratioDefault,
    resolution: f.resolutionDefault,
    outputs: f.outputs,
    model: DEFAULT_MODEL,
    primary: [],
    secondary: [],
    outputRefs: [],
    look: { ...MODEL_LOOK_DEFAULT },
    scene: { ...SCENE_DEFAULT },
  }
}

/**
 * 内嵌模式 —— 「抽屉里那一份」。
 *
 * 2026-10-09 用户口径（AI 模特库截图圈着「生成新模特」）：「这个生成模特最好是
 * 弹出右边栏目」。于是那一页不再跳到这里，改成从右边滑出抽屉**就地生成**。
 * 抽屉里渲染的**仍然是这一个组件** —— 不另写一份模特表单：同一件事两个实现
 * 迟早各自演化，「界面说的 / 报价算的 / 载荷发的」立刻变成三处各说一遍。
 *
 * 内嵌带来三个**必须在这个组件内部**处理的差别：
 *   · 抽屉里没有路由参数（当时的路由是 `/ai-models`）⇒ 类目不能读 `useParams`；
 *   · 档位由调用方**指死**（入口就叫「生成新模特」，用户在抽屉里还能切到
 *     「白底主图」就是声不对）⇒ 不渲染页签外那排 `pickbar`；
 *   · `setSp` 改的是**当前页**的 URL ⇒ 内嵌时一个字都不许写（见 `selectFeature`）。
 */
export type FashionStudioProps = {
  /** 给了它就是内嵌（抽屉）模式：类目与档位由调用方指定 */
  embed?: { category: string; feature: string }
}

export function FashionStudio({ embed }: FashionStudioProps = {}) {
  const { category } = useParams()
  const cat: 'products' | 'fashion' = embed
    ? embed.category === 'products'
      ? 'products'
      : 'fashion'
    : category === 'products'
      ? 'products'
      : 'fashion'
  const [sp, setSp] = useSearchParams()
  const features = useMemo(() => featuresOf(cat), [cat])
  const { value: form, patch, reset } = useDraft(`professional-${cat}`, defaultsFor(features[0]))
  // useLocalStorage 自带 hw_ 前缀；两个类目各存一份进度，互不覆盖。
  // ⚠️ key 带 -v2：页签由 2 个拆成 3 个后，老值 1（原意「核对与生成」）
  // 在新结构里落到「出图参数」—— 索引含义变了，留着就是错的。换 key 让老值失效，
  // 用户回到页签 1（也与「换工作流回到第一个页签」的既有规则一致）。
  const [step, setStep] = useLocalStorage<number>(`step_professional-${cat}-v2`, 0)
  const { assets } = useAssetLibrary()
  const { cancel, jobsFor, outputsFor, refCandidatesFor, clearResults } = useJobCenter()
  // 生成成功后自动清空工作区（表单 + 上传素材 + 右栏本轮产出）—— 见 GenerateFlowOptions.workspace
  const flow = useGenerateFlow({ workspace: { snapshot: form, reset } })
  const toast = useToast()

  /**
   * 模特设定里「外貌特征（高级设置）」的展开态。
   *
   * 折叠态是用户的一次点击、派生不出来，所以存 state；折叠头本身仍走 `CardHead`
   * （全站唯一一份实现，`open` 受控）。默认收起 —— 它是微调，不是每次都要填的东西。
   */
  const [lookAdvOpen, setLookAdvOpen] = useState(false)

  // URL 是唯一真源：?feature= 变化时同步表单，保证分享链接/刷新行为一致
  //
  // ⚠️ 内嵌（抽屉）时真源换成调用方给的 `embed.feature`。抽屉开在 `/ai-models` 上，
  // 那里没有 `?feature=` —— 照读 URL 会读到 null，于是**停在上一次那个档**
  // （草稿是整页共用一份的，用户上次在独立页选的是「白底主图」），
  // 表现就是「点生成新模特，抽屉里出来的是商品图表单」。
  const featureParam = embed ? embed.feature : sp.get('feature')
  useEffect(() => {
    const valid = features.find((f) => f.id === featureParam)
    if (valid && valid.id !== form.feature) {
      patch({
        feature: valid.id,
        outputs: valid.outputs,
        aspectRatio: valid.ratioDefault,
        resolution: valid.resolutionDefault,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureParam, cat])

  const feature = findFeature(cat, form.feature)
  const profile = getPlatform(form.platform)
  const sourcePage = `professional/${cat}`

  /**
   * app_slug 前缀**只有这一处** —— 报价、提交、`inputs.app_slug`，
   * 以及「候选参考图取自哪个工作流」都走它。分开写就是给
   * 「同一份枚举两处各写一遍」开门（本项目头号 bug 类型）。
   */
  const slugOf = (id: string) => `${cat === 'products' ? 'product' : 'fashion'}-${id}`
  const appSlug = slugOf(feature.id)

  /**
   * 本档的参考图是「从产出里挑」还是「用户上传」—— **唯一一份判断**。
   *
   * `primaryFrom`（指死某一档的产出）走 RefUploader 的候选条、**不出上传区**；
   * 其余档一律是用户上传。⚠️ 场景图两个都不是 —— 它跟 AI 模特图一样
   * 整块不渲染参考图（`textOnly`，见 `catalog.ts` 里那三条用户口径）。
   */
  const pickFromOutputs = Boolean(feature.primaryFrom)

  /**
   * 本档**收不收**参考图 —— `textOnly` 档（AI 模特图 / 场景图）一律不收。
   *
   * ⚠️ 这件事「界面上不渲染上传区」并不够。草稿是**整页共用**一份
   * （`professional-products`），而 `selectFeature` 刻意**不清空**已上传的素材
   * （换档只换参数、素材留着继续用 —— 「白底主图 → 卖点图」就是要这个行为）。
   * 于是从有参考图的档切到 AI 模特图时：界面上**没有**上传区、提示词里也写着
   * 「本档没有参考图」，但 `imageUrls` 仍把草稿里那张商品图发了出去，
   * 图生图模型照样把它画进画面。
   * 2026-09-30 用户报障原话：「为什么都有这个帽子的产品，你是不是写死了」
   * —— 帽子不是写死的，是他更早在同一页别的档传过的那张参考图。
   *
   * 收口点选在**派生层**、而不是去 `selectFeature` 里清空草稿：
   * 档的契约是「本档没有参考图」，那本档就不该有任何一张图出门，
   * 不管它在草稿里躺了多久、也不管别的档多需要它。
   * （父组件已经是这一条：`prompt` 里 `hasRef` 也只看 `refImages`。）
   */
  const acceptsRefs = !feature.textOnly

  /**
   * 本档的参考图 —— **唯一一份**。
   *
   * 来源不同、存放的字段也不同（`outputRefs` 是「从产出里挑的」，`primary` 是
   * 「用户上传的」），但页面只算这一个变量，下面（必填判定 / 提示词张数 /
   * 核对页 / 提交）一律读它。各处各写一遍三目的话，每多一处就多一个漏改点 ——
   * 而漏改的表现是「界面说 A、实际传 B」，截图上看不出来。
   */
  const refImages = acceptsRefs ? (pickFromOutputs ? form.outputRefs : form.primary) : []

  /** 第二参考图 —— 与 `refImages` 同一个 `acceptsRefs` 把关，别各判各的 */
  const secondaryImages = acceptsRefs ? form.secondary : []

  /* ------------------------------------------------------------------ *
   * 从商品库带入（2026-10-09）
   *
   * 用户口径：「其他页面也可以引用这个商品库啊」。这一页与别的页有两处不同，
   * 两处都是**这一页的字段结构决定的**，不是特例：
   *
   * 1. **文字落到 `requirement`（需求描述）而不是「产品名」** —— 这一页没有
   *    产品名字段（`/professional/*` 的档位表单由 feature 驱动）。
   *    ⚠️ 并且这里是**追加**、不是替换：`requirement` 是一段自由文本，用户很
   *    可能已经写了「模特站在白色背景前、全身入镜」这类构图要求，
   *    覆盖等于把他刚写的东西删掉 —— 而删的痕迹在界面上只是一次重绘。
   *    已经带过同一个商品（文本里出现它的标题）就不再重复追加。
   *
   * 2. **图只在「本档收参考图」且「参考图来源是用户上传」时才带** ——
   *    判据用既有的 `acceptsRefs`（`textOnly` 档一张都不许出去）与
   *    `pickFromOutputs`（那一档的参考图**只能**从上一档产出里挑，
   *    往 `primary` 里写图会变成一份界面看不见、却会被发出去的输入 ——
   *    正是 `acceptsRefs` 那段注释里用户报过的那个帽子问题）。
   * ------------------------------------------------------------------ */
  const applyProductAsset = (asset: ProductAsset) => {
    const block = [asset.title, asset.sellingPoints].filter(Boolean).join('\n')
    const current = form.requirement.trim()
    const nextText =
      !current || current.includes(asset.title) ? current || block : `${current}\n${block}`

    /* `primaryMax` 在 `textOnly` 档上是 `undefined`（那一档没有参考图槽位），
       所以先归一成 0，再拿它当「本档能不能收图」的第二个判据 —— 顺便也让 TS 满意。 */
    const maxRefs = feature.primaryMax ?? 0
    const canTakeImages = acceptsRefs && !pickFromOutputs && maxRefs > 0
    const { urls, dropped } = canTakeImages
      ? mergeProductRefs(form.primary, asset.images.map((i) => i.url).filter(Boolean), maxRefs)
      : { urls: [] as string[], dropped: 0 }

    patch(canTakeImages ? { requirement: nextText, primary: urls } : { requirement: nextText })
    toast.success(
      productImportMessage(asset.title, {
        refs: urls.length,
        dropped,
        max: maxRefs,
        what: '标题与卖点',
      }) + (canTakeImages ? '' : '（本档不收参考图，图没带进来）'),
    )
  }

  /** 参考图的来源工作流（`primaryFrom` 指向的那一档），用来出文案与跳转 */
  const sourceFeature = feature.primaryFrom
    ? features.find((f) => f.id === feature.primaryFrom)
    : undefined
  /* `primaryLabel` 现在是可选的（`textOnly` 档根本没有参考图），所以这里要二级兜底：
     真找不到名字时给一个中性词，**不要**让 `undefined` 漏进 UI 变成「取自本页「undefined」的产出」。
     指错档由 smoke-nav 在构建期拦住，这里只保证运行期不说假话。 */
  const sourceName = sourceFeature?.name ?? feature.primaryLabel ?? '上一档工作流'
  const sourceSlug = feature.primaryFrom ? slugOf(feature.primaryFrom) : undefined

  const candidates = useMemo(
    () => (sourceSlug ? refCandidatesFor(sourcePage, sourceSlug) : []),
    [refCandidatesFor, sourcePage, sourceSlug],
  )

  /**
   * 参考图的**另一条来源** —— 传了它，RefUploader 就**不出上传区**（见其 `source` 说明）。
   *
   * 目前**没有档位在用**（场景图 2026-09-30 起跟 AI 模特图一样整块不要参考图，
   * 见 `catalog.ts`）；留着是因为这条机制本身是好的：一旦某档的参考图只可能
   * 来自另一个档的产出，就该走它，而不是在页面上再写一个「产出选择器」。
   */
  const pickSource = feature.primaryFrom
    ? {
        name: sourceName,
        candidates,
        onGo: sourceFeature ? () => selectFeature(sourceFeature.id) : undefined,
      }
    : undefined

  /**
   * 模特设定文本 —— **派生值，不存 state**（存的是八个选择值，即 `form.look`）。
   * 全默认（不限 / 自动）时是空串，模板里的 `hasModelLook` 随之false、整段不输出。
   * 非 `modelLook` 档一律空串：那些档没有这套选择器，也就不该冒出这一段。
   */
  const modelLookValue = feature.modelLook ? modelLookText(form.look) : ''

  /**
   * 逐张出图清单 —— 同样是**派生值**（张数已在 `form.outputs` 里，不另存 state）。
   *
   * 真源是 `config/modelProfile.ts` 的 `modelShotPlan`，与提示词里的
   * 【逐张出图清单】、报价弹窗里那份 prompt 三处共用一个函数 ——
   * 页面里再抄一遍「第 2 张是三视图」的话，改张数时必然有一处漏改。
   */
  const shotPlan = feature.modelLook ? modelShotPlan(form.outputs) : []

  /** 场景设定文本 —— 同上，同理由 `feature.scenePlan` 把关（见 `config/sceneProfile.ts`） */
  const sceneValue = feature.scenePlan ? sceneText(form.scene) : ''

  /**
   * 本档的提示词输入 —— **一处组装、两处消费**：
   * `buildFeaturePrompt` 拿它出「真正发出去的提示词」，`buildFeatureFillBrief`
   * 拿它出「给 AI 看的骨架」。
   *
   * 为什么必须共用同一个对象：这两个函数在 `config/prompts.ts` 里读的是**同一套**
   * 模板变量（`featureTemplateVars`）。页面里各组装一份的话，迟早出现「AI 照着 A
   * 骨架写、真发出去的是 B」—— 对着截图看不出来的那类错（2026-10-04 抽出）。
   */
  const promptInput = useMemo(
    () => ({
      feature: feature.id,
      category: cat,
      platform: form.platform,
      requirement: form.requirement,
      modelLook: modelLookValue,
      scene: sceneValue,
      aspectRatio: form.aspectRatio,
      resolution: form.resolution,
      outputs: form.outputs,
      primaryCount: refImages.length,
      secondaryCount: form.secondary.length,
    }),
    [feature.id, cat, form.platform, form.requirement, modelLookValue, sceneValue, form.aspectRatio, form.resolution, form.outputs, refImages.length, form.secondary.length],
  )

  const prompt = useMemo(() => buildFeaturePrompt(promptInput), [promptInput])

  /** 交给 AI 的上下文 —— 与 `prompt` 同一份输入，所以 AI 看到的就是真会发出去的那份结构 */
  const fillBrief = useMemo(() => buildFeatureFillBrief(promptInput), [promptInput])

  /**
   * 不让点「AI 补全」的原因。**有依据才让它写** ——
   * 这一档没有「产品名」输入框，模型也看不到图，它能用的材料只有三样：
   * 参考图、用户已写的字、模特/场景设定。三者全空时它只能编一段与产品无关的套话，
   * 那比不给按钮更糟（用户还得自己判断哪些是假的）。
   *
   * ⚠️ **不因为「本档文字必填」就禁用**：`textRequired` 的档（如卖点图，
   * 一行一个卖点、最多 6 行）恰恰是最需要 AI 起草的，只要给了参考图就放开。
   */
  const hasFillInput =
    refImages.length > 0 ||
    form.requirement.trim().length > 0 ||
    Boolean(modelLookValue) ||
    Boolean(sceneValue)
  const fillBlocked = hasFillInput
    ? undefined
    : `先上传${feature.primaryLabel ?? '参考图'}，或写一句你能提供的产品信息`

  const price = usePricePreview({
    appSlug,
    model: form.model,
    outputs: form.outputs,
    resolution: form.resolution,
    aspectRatio: form.aspectRatio,
  })

  /**
   * 右栏取数**必须带第二参数 `appSlug`**（2026-09-29 用户报障）。
   *
   * 本页 8 个（服饰 8 个）工作流共用一个 `sourcePage`，靠 `app_slug` 区分：
   * `product-product-main` / `product-product-model` / `product-product-scene` …
   * 只按页面键过滤的话，用户切到「场景图」，右栏标题写着「场景图 · 结果」，
   * 里面却是上一档 AI 模特图跑出来的 2 张 —— 用户原话「这商品图是 AI 模特图」。
   *
   * ⚠️ 别把这里当成「多传一个键」的冗余：`appSlug` 本来就是本页算好的
   * （报价、提交、`inputs.app_slug` 都用它），传下去的只是**同一个值**，
   * 没有新增第二处要同步的键 —— 这正是本项目头号 bug 类型要防的东西。
   */
  const jobs = jobsFor(sourcePage, appSlug)
  const outputs = outputsFor(sourcePage, appSlug)

  /* ---- 必填判定：全部从 feature 读，页面不再自己写死 ---- */
  const primaryOk = !feature.primaryRequired || refImages.length > 0
  const secondaryOk = !feature.secondaryRequired || form.secondary.length > 0
  const textOk = !feature.textRequired || form.requirement.trim().length > 0
  const formOk = primaryOk && secondaryOk && textOk
  const canSubmit = formOk

  const missingReason = !primaryOk
    ? pickSource
      ? // 这一档没有上传通道，说「请先上传」等于指着一条不存在的路
        candidates.length
        ? `请先选中一张${feature.primaryLabel}`
        : // 本页还没跑过来源那一档 —— 直接说去哪生成，比「请先上传」可执行
          `请先去「${sourceName}」生成一张`
      : `请先上传${feature.primaryLabel}`
    : !secondaryOk
      ? `请先上传${feature.secondaryLabel}`
      : !textOk
        ? `请填写${feature.textLabel}`
        : undefined


  // 图床状态：上传后要能一眼看出这张图有没有拿到公网地址
  // （图床挂了不影响出图，但生成时会重新上传一次，耗时会长一点，得让用户知道）
  const uploadHost = useMemo(() => {
    const map = new Map(assets.map((a) => [a.url, a.host]))
    return (urls: string[]) => urls.map((u) => map.get(u)).find(Boolean)
  }, [assets])
  const primaryHost =
    refImages.length && !pickFromOutputs ? uploadHost(refImages) : undefined

  const selectFeature = (id: string) => {
    const f = features.find((x) => x.id === id)!
    patch({
      feature: id,
      outputs: f.outputs,
      aspectRatio: f.ratioDefault,
      resolution: f.resolutionDefault,
    })
    // ⚠️ 内嵌（抽屉）里**不写 URL**：那时当前路由是 `/ai-models`，
    // `setSp` 会把它改成 `/ai-models?feature=…` —— 抽屉里的动作改到了宿主页面身上。
    // 内嵌时不渲染 `pickbar`，正常到不了这里；留着这道闸是防「哪天有人把下拉接回来」。
    if (!embed) setSp({ feature: id }, { replace: true })
    // 换工作流等于换一套素材要求，回到第一个页签重新进料
    setStep(0)
  }

  /* ------------------------- 页签 1 · 参考素材 ------------------------- */

  /**
   * 模特设定面板 —— **只有 `feature.modelLook` 的档渲染**（目前是 AI 模特图）。
   *
   * 2026-09-30 用户口径：这一档的输入不该是「一个自由文本框 + 一堆商品说明」，
   * 而是「模特设置 → 外貌特征（高级设置） → 姿势与场景 → 补充要求」。
   *
   * 分组、顺序、标签、选项**全部从 `config/modelProfile.ts` 读**，
   * 这里一个选项字面量都不写 —— 页面里再抄一份的话，改选项要记得回来改页面，
   * 漏改的表现是「界面能选、提示词里没有」，截图上看不出来（本项目头号 bug 类型）。
   */
  const modelLookPanel = !feature.modelLook ? null : (
    <div className="modellook">
      {MODEL_LOOK_GROUPS.map((group) => {
        const rows = group.fields.map((f) => (
          <Field key={f.key} label={f.label}>
            <Chips
              value={form.look[f.key]}
              /* 计算键会让 TS 丢掉具名属性，这里断言回 ModelLook ——
                 值是 `f.options` 里的一项，来源由 `modelProfile.ts` 保证 */
              onChange={(v) => patch({ look: { ...form.look, [f.key]: v } as ModelLook })}
              options={f.options.map((o) => ({ value: o, label: o }))}
            />
          </Field>
        ))

        /* 高级设置默认收起：摊开会把「目标市场 / 性别 / 年龄」这些每次都要
           确认的项挤到首屏以下。折叠头用 `CardHead`（全站唯一一份折叠实现），
           容器 `.inlinefold` 只把它的默认 padding 收掉。 */
        return group.advanced ? (
          <div className="modellook__adv inlinefold" key={group.id}>
            <CardHead
              title={group.title}
              right={<span className="muted" style={{ fontWeight: 400 }}>高级设置</span>}
              open={lookAdvOpen}
              onToggle={() => setLookAdvOpen((v) => !v)}
            />
            {lookAdvOpen ? (
              <>
                {group.hint ? (
                  <div className="field__hint" style={{ marginBottom: 8 }}>
                    {group.hint}
                  </div>
                ) : null}
                {rows}
              </>
            ) : null}
          </div>
        ) : (
          <div className="modellook__group" key={group.id}>
            <div className="modellook__title">{group.title}</div>
            {group.hint ? (
              <div className="field__hint" style={{ marginBottom: 8 }}>
                {group.hint}
              </div>
            ) : null}
            {rows}
          </div>
        )
      })}
    </div>
  )

  /**
   * 场景设定面板 —— **只有 `feature.scenePlan` 的档渲染**（目前是场景图）。
   *
   * 2026-09-30 用户口径：场景图的输入 =「商品图 → 场景类型 → 场景风格 → 场景描述」。
   * 分组、选项、二级空间**全部从 `config/sceneProfile.ts` 读**，
   * 这里一个选项字面量都不写 —— 页面里再抄一份的话，改选项要记得回来改页面，
   * 漏改的表现是「界面能选、提示词里没有」（本项目头号 bug 类型）。
   *
   * 二级只在当前类型给了 `subs` 时渲染（选「居家」才有客厅 / 卧室…）。
   * 换类型时**清空二级**：不清的话「办公 · 卧室」会留在表单里、提示词照写，
   * 而界面上那一行已经消失 —— 截图上看不出来的那种错。
   */
  const sceneSubs = subsOf(form.scene.type)
  const scenePanel = !feature.scenePlan ? null : (
    <div className="modellook">
      <div className="modellook__group">
        <div className="modellook__title">场景类型</div>
        <div className="field__hint" style={{ marginBottom: 8 }}>
          产品放在哪 —— 选「居家」还能挑到具体空间
        </div>
        <Field label="场景类型">
          <Chips
            value={form.scene.type}
            onChange={(v) => patch({ scene: { ...form.scene, type: v, sub: '' } })}
            options={SCENE_TYPES.map((t) => ({ value: t.value, label: t.label }))}
          />
        </Field>
        {sceneSubs.length ? (
          <Field label="具体空间">
            <Chips
              value={form.scene.sub || null}
              onChange={(v) => patch({ scene: { ...form.scene, sub: v } })}
              options={sceneSubs.map((s) => ({ value: s, label: s }))}
            />
          </Field>
        ) : null}
      </div>
      <div className="modellook__group">
        <div className="modellook__title">场景风格</div>
        <div className="field__hint" style={{ marginBottom: 8 }}>
          「AI 智能匹配」= 交给模型按品类判断
        </div>
        <Field label="场景风格">
          <Chips
            value={form.scene.style}
            onChange={(v) => patch({ scene: { ...form.scene, style: v } })}
            options={SCENE_STYLES.map((s) => ({ value: s, label: s }))}
          />
        </Field>
      </div>
    </div>
  )

  /*
    这一整块是「产品信息」section —— 走全站唯一一份 `ProductInfoSection`。
    服饰图没有「产品名称」字段（表单由 feature 驱动），所以不传 `name`：
    它借这张卡的外壳，把**主参考图**放进 `refs` 槽（先给素材），其余
    （第二参考图 + 模特 / 场景设定 + 补充要求）放进 `foot`（排在主参考图之后）。
    `refsFirst` 保证顺序仍是「图在前、文字在后」，与改造前逐块一致。
  */
  const stepAssets = (
    <ProductInfoSection
      title={feature.name}
      icon="shirt"
      refsFirst
      headRight={
        <>
          {/* 「从商品库选择」—— 这一页没有「产品名」字段（表单由 feature 驱动），
              所以入口挂在卡片头上，带入的是**需求描述 + 主参考图**。
              按钮与「AI 补全」并列：一个从自己的库里取，一个让 AI 现写。 */}
          <ProductAssetPickerButton onPick={applyProductAsset} />
          <Badge tone="primary">{cat === 'products' ? '全品类' : '服饰'}</Badge>
        </>
      }
      /*
        `textOnly` 的档（AI 模特图 / 场景图）**整块不渲染参考图** ——
        它的输入只有文字与选择器，商品图出现在这里与用户目的无关。
        注意是「不渲染」而不是 `hidden`：留个空壳会撑出一段没有内容的间距。
        2026-09-30 用户口径：「场景图这块跟 ai 模特一样，不需要商品图什么的」。
        ⚠️ 判据仍是 `feature.textOnly` —— 不传 `onRefsChange`，这一槽整块不渲染。
      */
      refs={refImages}
      onRefsChange={
        feature.textOnly
          ? undefined
          : (next) => patch(pickFromOutputs ? { outputRefs: next } : { primary: next })
      }
      refsLabel={feature.primaryLabel}
      refsRequired={feature.primaryRequired}
      refsHint={
        pickSource
          ? `取自本页「${sourceName}」的产出 · 输出 ${form.outputs} 张`
          : `最多 ${feature.primaryMax} 张 · 输出 ${form.outputs} 张`
      }
      refsMax={feature.primaryMax}
      /* 单张参考图没有主副之分，角标会让人以为还有一组副图（与视频页
         「场景参考图」同一个理由，见 RefUploader 的 firstBadge 说明） */
      refsFirstBadge={pickSource ? '' : '主图'}
      /* ⚠️ `pickSource` 存在就**一定**走候选条、不回落上传区：
         否则一旦指向了不存在的档（或漏配），用户看到的是一个完全正常的上传框，
         「本档参考图只能从产出里挑」这件事被静默丢掉（**没渲染 ≠ 报错**）。
         这是用户报过两次的同一件事（见 RefUploader 的 `source` 说明）。 */
      refsSource={pickSource}
      refsDropLabel="拖拽或点击上传"
      refsDropHint={`${feature.primaryRequired ? '主参考图' : '不传也能生成 —— 全部信息由文字描述提供'} · 多选 · 可拖拽排序`}
      refsFootnote={
        refImages.length && !pickFromOutputs
          ? primaryHost
            ? `已入图床 ${imgHostLabel(primaryHost)}`
            : '本地暂存（图床不可用，生成时重试）'
          : undefined
      }
      foot={
        <>
          {feature.secondaryLabel ? (
            <RefUploader
              wrap="none"
              label={feature.secondaryLabel}
              required={feature.secondaryRequired}
              urls={form.secondary}
              onChange={(secondary) => patch({ secondary })}
              max={feature.secondaryMax ?? 1}
              dropLabel="拖拽或点击上传"
              dropHint="支持多选 · 可拖拽排序"
            />
          ) : null}

          {modelLookPanel}

          {scenePanel}

          {feature.textLabel ? (
            <Field
              label={feature.textLabel}
              required={feature.textRequired}
              hint={feature.textHint}
              right={
                /* 「AI 补全」—— 与 `/banner` `/video` 等六页共用同一份实现
                   （`components/AiFillButton`，本项目「交互控件只许一份实现」那条）。

                   本页与那六页的唯一差别在**上下文**：这里多带了**模板骨架**。
                   因为这一格在不同档里语义完全不同 —— 白底主图那格是「补充要求」、
                   卖点图那格是「卖点内容」（一行一条、条数配出图张数）、
                   服装上身那格是「文字补充要求」。不给骨架，模型写出来的东西
                   放哪一档都别扭（见 `buildFeatureFillBrief` 的头注）。 */
                <AiFillButton<FormState>
                  kind="feature"
                  /* 白名单取自跨前后端的真源 —— 与后端 `_FILL_FIELDS["feature"]` 逐字一致 */
                  fields={FILL_FORM_FIELDS.feature}
                  brief={fillBrief}
                  sourcePage={sourcePage}
                  /* 快照必须在**覆盖前**取 —— 组件里再取就已经是 AI 写过的版本了 */
                  snapshot={{ requirement: form.requirement }}
                  onFill={patch}
                  blocked={fillBlocked}
                  label="AI 补全"
                />
              }
            >
              <Textarea
                value={form.requirement}
                rows={4}
                placeholder={feature.textPlaceholder}
                onChange={(e) => patch({ requirement: e.target.value })}
              />
            </Field>
          ) : null}
        </>
      }
    />
  )

  /* ------------------------- 页签 2 · 出图参数 ------------------------- */

  const stepConfig = (
    <Card>
      <CardHead title="出图参数" icon="bolt" />
      <CardBody>
        <ModelSelect
          kind="image"
          value={form.model}
          onChange={(v) => patch({ model: v })}
          hasRef={refImages.length > 0}
        />
        <Field
          label="投放平台"
          hint={`图内文字 ${profile.textLanguage}${profile.textInImage ? '' : '（该平台主图不允许文字）'}`}
        >
          <Select
            value={form.platform}
            onChange={(e) => {
              const next = getPlatform(e.target.value)
              // 比例跟着平台走：Amazon 主图 1:1、Ozon 3:4、TikTok 竖版
              patch({ platform: next.id, aspectRatio: next.sizes.main })
            }}
          >
            {PLATFORM_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.list.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </Field>
        <Field label="画面比例">
          {/* 画面比例 = 下拉（本档常用）+ 自由输入框（想出的比例不在清单里时直接敲）。
              2026-10-10 用户口径「这些……都可以自定义比例」—— 上一版是纯档位按钮组，
              用户想出的比例不在其中时**根本没有入口**。控件是 `RatioInput`，
              与视频页那份**同一实现**（全站铁律：同一控件只许一份）。 */}
          <RatioInput
            value={form.aspectRatio}
            onChange={(v) => patch({ aspectRatio: v })}
            options={feature.ratioOptions.map((o) => o.value)}
          />
        </Field>
        <Field label="分辨率">
          <Segmented
            value={form.resolution}
            onChange={(v) => patch({ resolution: v as FormState['resolution'] })}
            options={IMAGE_RESOLUTIONS}
          />
        </Field>
        <Field label="输出数量" hint={`本工作流建议 ${feature.outputs} 张`}>
          {/* 张数 = 常用档位（1-4）+ 自定义：2026-10-10 用户口径「出图数量 1-4，
              然后都可以自定义数量」。档位来自本档配置（`feature.outputOptions`），
              想要别的张数直接敲旁边的数字框，不再被锁在几个预设上。 */}
          <CountInput
            value={form.outputs}
            onChange={(n) => patch({ outputs: n })}
            presets={feature.outputOptions}
          />
        </Field>
      </CardBody>
    </Card>
  )

  /* ------------------------- 页签 3 · 核对与生成 ------------------------- */

  const stepReview = (
    <>
      <Card>
        <CardHead title="参数核对" icon="check" />
        <CardBody tight>
          <div className="review-list">
            <div className="review-list__row">
              <span className="review-list__k">工作流</span>
              <span className="review-list__v">
                {feature.name} <span className="muted">· {feature.description}</span>
              </span>
            </div>
            {feature.primaryLabel ? (
              <div className="review-list__row">
                <span className="review-list__k">{feature.primaryLabel}</span>
                <span className={`review-list__v${refImages.length ? '' : ' review-list__v--muted'}`}>
                  {refImages.length
                    ? `${refImages.length} 张`
                    : feature.primaryFrom
                      ? '未选择'
                      : '未上传'}
                </span>
              </div>
            ) : null}
            {feature.secondaryLabel ? (
              <div className="review-list__row">
                <span className="review-list__k">{feature.secondaryLabel}</span>
                {/* 核对页必须报「真正会发出去的那几张」——读 `secondaryImages`
                    而不是草稿字段，否则界面说 A、实际传 B（本项目最难查的一类） */}
                <span className={`review-list__v${secondaryImages.length ? '' : ' review-list__v--muted'}`}>
                  {secondaryImages.length ? `${secondaryImages.length} 张` : '未上传'}
                </span>
              </div>
            ) : null}
            {feature.modelLook ? (
              <div className="review-list__row">
                <span className="review-list__k">模特设定</span>
                <span className={`review-list__v${modelLookValue ? '' : ' review-list__v--muted'}`}>
                  {/* 没选就是空串，不能显示成空白行 —— 那看起来像「界面坏了」。
                      这里如实说它是「没指定」，与提示词里整段不输出是同一件事。 */}
                  {modelLookValue || '未指定（由模型自行判断）'}
                </span>
              </div>
            ) : null}
            {/* 逐张产出 —— 与提示词里的【逐张出图清单】**同源**（`modelShotPlan`）。
                摆在这里是因为「出几张」不等于「出什么」：2 张里第 2 张是三视图这件事，
                不先看一眼就会在花了钱之后才发现（2026-09-30 用户口径）。 */}
            {shotPlan.length ? (
              <div className="review-list__row">
                <span className="review-list__k">逐张产出</span>
                <div className="review-list__v">
                  {shotPlan.map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                </div>
              </div>
            ) : null}
            {feature.scenePlan ? (
              <div className="review-list__row">
                <span className="review-list__k">场景设定</span>
                <span className={`review-list__v${sceneValue ? '' : ' review-list__v--muted'}`}>
                  {/* 与「模特设定」同一条规矩：没选就如实说「没指定」，
                      不显示成空白行（那看起来像界面坏了），也与提示词里整段不输出对齐 */}
                  {sceneValue || '未指定（由模型按品类判断）'}
                </span>
              </div>
            ) : null}
            {feature.textLabel ? (
              <div className="review-list__row">
                <span className="review-list__k">文字描述</span>
                <span className={`review-list__v${form.requirement.trim() ? '' : ' review-list__v--muted'}`}>
                  {form.requirement.trim()
                    ? `${form.requirement.trim().length} 字`
                    : '未填写'}
                </span>
              </div>
            ) : null}
            <div className="review-list__row">
              <span className="review-list__k">投放平台</span>
              <span className="review-list__v">
                {profile.name}
                <span className="muted"> · 图内文字 {profile.textLanguage}</span>
              </span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">模型与规格</span>
              <span className="review-list__v">
                {modelLabel(form.model)}
                <span className="muted">
                  {' '}
                  · {form.outputs} 张 · {form.aspectRatio} · {form.resolution}
                </span>
              </span>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ⚠️ 这里**不要**再加「提示词预览」卡。
          2026-10-04 本改动加过一次，被 `nav:test` 当场拦下 —— 规则在
          `scripts/smoke-nav.ts`（`PROMPT_CARD_TITLES`）：提示词只许在
          `PriceConfirmDialog` 里展示一份。
          理由（2026-09-29 定的，仍然成立）：页面内那份会撑掉一屏、正好压在
          提交按钮上方，而且用户会以为它是可编辑的输入区；展示格式也会变成要改两处。
          **复核要发出去什么，位置就该是「花钱前那一刻」** —— 点生成、弹窗里看。 */}
    </>
  )

  const stepBody = [stepAssets, stepConfig, stepReview][Math.min(step, PANES.length - 1)]

  /**
   * 页签标题随档走 —— `textOnly` 的档**没有参考素材**（`catalog` 的 `textOnly`），
   * 第一屏里全是选择器，标题还写「参考素材」的话用户会以为素材区丢了。
   *
   * 两档的口径不同，所以标题按**选择器**分（不能只看 `textOnly`）：
   *   · AI 模特图（`modelLook`）→「模特设置」
   *   · 场景图（`scenePlan`）  →「场景设置」
   * 只看 `textOnly` 的话，场景图那一档会被叫成「模特设置」—— 而它这一档
   * 恰恰不许出现人（用户口径：「人相关的全部删掉」）。
   *
   * ⚠️ `PANES` 常量本身**不动**（页签数/顺序/desc 是跨档共享的，冒烟也按它数）；
   * 这里只替换第一项的文案，数量与顺序逐项一致 —— 所以**不必**升 `step_*` key，
   * 老的「停在第 2 个页签」仍落在「出图参数」上。
   */
  const firstTab: WorkflowTab = feature.modelLook
    ? { ...PANES[0], title: '模特设置', desc: '模特地区、性别、年龄、体型与气质，以及姿势与场景' }
    : feature.scenePlan
      ? { ...PANES[0], title: '场景设置', desc: '场景类型、空间与风格，以及可选的场景描述' }
      : PANES[0]
  const tabs: WorkflowTab[] = [firstTab, PANES[1], PANES[2]]

  /* ------------------------------ 底部导航 ------------------------------ */

  const submitDock = (
    <Card className="studio__dock">
      <CardBody tight>
        <SubmitBar
          disabled={!canSubmit}
          disabledReason={!canSubmit ? `请先补充：${missingReason ?? ''}` : undefined}
          label={`生成 ${feature.name}`}
          onGenerate={() =>
            flow.request({
              info: {
                title: feature.name,
                appSlug,
                model: form.model,
                platform: `${profile.name} · 图内文字 ${profile.textLanguage}`,
                outputs: form.outputs,
                aspectRatio: form.aspectRatio,
                resolution: form.resolution,
                refImages: refImages.length + secondaryImages.length,
                prompt,
                price: price?.estimatedPrice ?? 0,
                basis: price?.basis,
                free: price?.isFreeThisCall,
              },
              args: {
                appSlug,
                title: `${feature.name} · ${refImages.length ? `${refImages.length} 张素材` : '纯文字'}`,
                sourcePage,
                model: form.model,
                platform: form.platform,
                prompt,
                /* `inputs` 是表单快照，会原样落库。本档不收参考图时**记录里也不留**
                   —— 带着一张永远不会提交的图，事后翻任务记录只会误导人。
                   ⚠️ 后端 `aigc.py` 有「顶层 imageUrls 为空就往 inputs 里兜」的兜底，
                   但它兜的键是 imageUrls / refs / referenceImages，`primary` 不在其中；
                   真哪天有人把 `primary` 加进兜底清单，本档就会原地复活这个 bug。 */
                inputs: {
                  ...form,
                  ...(acceptsRefs ? {} : { primary: [], secondary: [] }),
                  __title: feature.name,
                  feature: feature.id,
                  category: cat,
                },
                imageUrls: [...refImages, ...secondaryImages],
                aspectRatio: form.aspectRatio,
                resolution: form.resolution,
                outputs: form.outputs,
                estimatedMs: feature.estimatedMs,
              },
            })
          }
        />
      </CardBody>
    </Card>
  )

  /**
   * 页签外的档位下拉 —— **内嵌（抽屉）时整块不渲染**。
   *
   * 抽屉那个入口叫「生成新模特」，档位已经由调用方指死；在抽屉里还能切到
   * 「白底主图」就是声不对（用户点的是「生成模特」）。⚠️ 是「不渲染」而不是
   * `hidden`：留个空壳会撑出一段没有内容的间距。
   *
   * ⚠️ 抽成变量只是为了让它能整个为 `null`；下拉本身一个字没改（见下面那段
   * 2026-10-08 的注释），`selectFeature` 仍是同一个函数。
   */
  const pickbar = embed ? null : (
      <div className="pickbar" style={{ marginBottom: 18 }}>
        {/* 类目标识 —— 原来是排头一颗静态 chip，收成下拉后由这枚灰字接回来 */}
        <span className="pickbar__label">{CATEGORY_LABELS[cat]}</span>
        <GroupedSelect
          value={feature.id}
          onChange={selectFeature}
          groups={[
            {
              id: cat,
              title: `${CATEGORY_LABELS[cat]} · 共 ${features.length} 种`,
              options: features.map((f) => ({ value: f.id, label: f.name })),
            },
          ]}
          ariaLabel="切换工作流"
        />
      </div>
  )

  return (
    <div className={embed ? 'studio-embed' : 'page page--wide'}>
      {/*
        工作流选择 —— **页签外的根决策，也是全页唯一的入口**。

        2026-10-08 用户口径：截图圈着这排 chip 说「这些 tag 可能要变成 option
        好点，现在有点搞复杂了」。原样是 1 枚类目 chip + 10 枚工作流 chip
        横铺一整行（`products` 类目 8 枚），每枚都带边框与内距 ——
        一屏里视觉最重的东西，恰恰是这排**还没开始干活的选择器**。

        收成一个原生下拉之后：
          · 闭合态只显示**当前这一档**，顶部元素从 11 个降到 1 个，
            且不再随档位增多而长高；
          · 类目标识（「专业服饰 / 全品类商品」）**没有丢** —— 它成了 `optgroup`
            的分组标题，展开即见，闭合态不必再占一枚 chip；
          · `onChange` 直接复用 `selectFeature`：换档重算张数 / 比例 / 分辨率、
            并回到第一个页签的行为**一个字没改**。改的只是控件的皮，不是语义。

        ⚠️ 位置**必须留在页签外**（`GeneratorFrame` 之上）。塞进页签 1 的话，
        切到「出图参数 / 核对与生成」就改不了工作流了 —— 这排原来刻意常驻，
        就是为了让用户在任何页签下都能换档。

        ⚠️ 选项**直接映射 `features`**（`featuresOf(cat)` 的结果），
        不做任何过滤、不写第二份清单：筛子集就是第二份真源，
        而它失效时不报错，只是「某一档再也选不到」。
      */}
      {pickbar}

      <GeneratorFrame
        form={
          <WorkflowTabs tabs={tabs} current={step} onChange={setStep}>
            {stepBody}
          </WorkflowTabs>
        }
        panel={
          <>
            {submitDock}
            {/* 这里原来挂了一张「全部工作流 · N 个」的 tool-grid 卡。
                它和左栏页签外那排 chip **是同一份清单的第二个入口** ——
                同一个 selectFeature、同一批 features，用户在两处看到两份一样的列表，
                右栏还被它挤掉了一整块结果区。工作流切换只保留页签外那一排
                （那一排常驻可见，切到「核对与生成」页签也还能换工作流）。

                ⚠️ 这段注释 2026-10-09 之前是**裸写的块注释标记**（没包花括号），
                React 把它整段当文本节点渲染出来了 —— 右栏出现一大坨「乱码」。
                JSX 子节点里的注释一律要用花括号包住。 */}
            <StudioPanel
              jobs={jobs}
              outputs={outputs}
              onCancel={cancel}
              onClear={clearResults}
              resultTitle={`${feature.name} 结果`}
              /* 历史也收窄到当前档（见上面 `jobsFor(sourcePage, appSlug)`），
                 标题必须跟着变 —— 卡片里只剩这一档的记录，还写「本页历史」就是假话。 */
              historyTitle={feature.name}
              /* 空态要说**这一档真实的第一步**：上传参考图的档说上传，
                 参考图取自其它档的说去跑那一档，`textOnly` 的档（没有参考图）
                 说去填选择器 —— 两档的选择器不同，所以文案也跟着分：
                 场景图说「场景」，AI 模特图说「模特」。说「上传」的话用户
                 在本档找不到上传位，会以为页面坏了。 */
              resultEmpty={
                feature.primaryFrom
                  ? `先跑一张${sourceName}，确认参数后点「生成 ${feature.name}」。${feature.description}`
                  : feature.textOnly
                    ? `填好上面的${
                        feature.scenePlan ? '场景要求' : '模特与场景要求'
                      }，确认参数后点「生成 ${feature.name}」。${feature.description}`
                    : `${feature.primaryRequired ? `上传${feature.primaryLabel}` : '可选上传素材'}后确认参数后点「生成 ${feature.name}」。${feature.description}`
              }
              aspectRatio={form.aspectRatio}
              /* 历史记录上的「重做」把该次参数填回左栏（见 lib/refill.ts）。
                 ⚠️ `feature` 也在表单里（`feature: f.id`），回填时会一起切回原来那一档。 */
              onRedo={(job: Job) => {
                patch(refillFromInputs(form, job.inputs))
                toast.success('已把这次任务的参数填回左侧表单 —— 确认后可再次生成')
              }}
            />
          </>
        }
      />

      <PriceConfirmDialog info={flow.pending} onCancel={flow.cancel} onConfirm={flow.confirm} />
    </div>
  )
}

/**
 * 路由壳：按 category 强制重挂载。
 *
 * 不这么做的话，从 /professional/fashion 切到 /professional/products 时
 * React 复用同一个组件实例，useDraft 的 state 还是服饰那一套，
 * 草稿键、选中项、输出数量全错位。
 */
export function FashionStudioRoute() {
  const { category } = useParams()
  return <FashionStudio key={category ?? 'fashion'} />
}
